import { cn } from '@/lib/utils';
import type { SingleMetricStats } from '@/types/metrics';

interface PeriodStatsRowProps {
  stats: SingleMetricStats;
  format: (v: number) => string;
  invertDelta?: boolean;
  loading?: boolean;
}

export function PeriodStatsRow({ stats, format, invertDelta, loading }: PeriodStatsRowProps) {
  if (loading) {
    return (
      <div className="dls-grid dls-grid-cols-2">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={cn(
              'dls-h-4 dls-w-14 dls-bg-muted dls-animate-pulse dls-rounded',
              i % 2 === 1 ? 'dls-mr-2' : 'dls-ml-2',
              i <= 2 ? 'dls-mb-0.5' : 'dls-mt-0.5'
            )}
          />
        ))}
      </div>
    );
  }

  // Hide when no meaningful data
  if (stats.high === 0 && stats.avg === 0 && stats.low === 0 && stats.delta === null) {
    return null;
  }

  const hasDelta = stats.delta !== null && stats.delta !== 0;
  const deltaPositive = stats.delta !== null && stats.delta > 0;
  const isImprovement = invertDelta ? !deltaPositive : deltaPositive;

  return (
    <div className="dls-inline-grid dls-grid-cols-2 dls-text-sm dls-leading-snug">
      <span className="dls-text-muted-foreground dls-pr-3 dls-pb-0.5 dls-border-r dls-border-b dls-border-border">
        H: <span className="dls-font-medium dls-text-foreground">{format(stats.high)}</span>
      </span>
      <span className="dls-text-muted-foreground dls-pl-3 dls-pb-0.5 dls-border-b dls-border-border">
        Avg: <span className="dls-font-medium dls-text-foreground">{format(stats.avg)}</span>
      </span>
      <span className="dls-text-muted-foreground dls-pr-3 dls-pt-0.5 dls-border-r dls-border-border">
        L: <span className="dls-font-medium dls-text-foreground">{format(stats.low)}</span>
      </span>
      {hasDelta ? (
        <span
          className={cn(
            'dls-font-medium dls-whitespace-nowrap dls-pl-3 dls-pt-0.5',
            isImprovement ? 'dls-text-status-online' : 'dls-text-status-offline'
          )}
        >
          {deltaPositive ? '\u25B2' : '\u25BC'}
          {deltaPositive ? '+' : ''}
          {format(stats.delta!)}
        </span>
      ) : (
        <span className="dls-pl-3 dls-pt-0.5" />
      )}
    </div>
  );
}
